<template>
  <div v-click-outside="closeSearch" class="search-box-two">
    <div
      v-if="hasOverlay && showOverlay && !isMobileOrTablet"
      class="overlay"
      @click="closeSearchBox"
    />
    <LazySearchBoxTwoInput
      :show-search="showSearch"
      :auto-focus="isMobileOrTablet ? false : focus"
      :discovery-search="discoverSearch"
      :discover-city-name="discoverCityName"
      :isMobile="isMobileOrTablet"
      v-model:text="searchedText"
      @open-search-box="openSearch"
      @focused="inputFocused"
      @searchInCity="changeSearchApi(true)"
      @searchInAllCities="changeSearchApi"
    />

    <transition
      @before-enter="beforeEnter"
      @enter="enter"
      @leave="leave"
      name="collapse-search-result"
      mode="out-in"
    >
      <div ref="dropdownRef" v-if="showResponseDropdown" class="drop-down">
        <SearchBoxTwoResult
          :searched-text="searchedText"
          :cities="topCities"
          :search-result="searchResult"
          :loading="loading"
          :searchInCity="searchInCityActivate"
        />
      </div>
    </transition>

    <BaseTopSheet
      v-if="isMobileOrTablet"
      ref="topSheetStatus"
      @close="closeSearchBox"
    >
      <div>
        <div class="flex items-center px-5">
          <LazySearchBoxTwoInput
            class="!w-full"
            :show-search="true"
            :auto-focus="focus"
            :discovery-search="discoverSearch"
            :discover-city-name="discoverCityName"
            :isMobile="true"
            :resetCityFilter="resetCityFilter"
            v-model:text="searchedText"
            @open-search-box="openSearch"
            @focused="inputFocused"
            @searchInCity="changeSearchApi(true)"
            @searchInAllCities="changeSearchApi(false)"
          />
        </div>
        <div class="result">
          <LazySearchBoxTwoResult
            :searched-text="searchedText"
            :cities="topCities"
            :search-result="searchResult"
            :loading="loading"
            :searchInCity="searchInCityActivate"
            :isMobileOrTablet="true"
          />
        </div>
      </div>
    </BaseTopSheet>
  </div>
</template>

<script setup lang="ts">
import {
  nextTick,
  onMounted,
  onUnmounted,
  onDeactivated,
  type PropType,
  ref,
  watch,
} from "vue";
import { useRoute } from "nuxt/app";
import type {
  IApiResponse,
  ITopCities,
  ISearchResult,
} from "../../types/general";
import useCustomFetch from "../../composables/useCustomFetch";
import SearchBoxTwoResult from "./SearchBoxTwoResult.vue";

const route = useRoute();
const emit = defineEmits(["updateFullWidthStatus"]);
const props = defineProps({
  isBox: {
    type: Boolean,
    default: false,
  },
  justIcon: {
    type: Boolean,
    default: false,
  },
  hasOverlay: {
    type: Boolean,
    default: true,
  },
  topCities: { type: Array as PropType<ITopCities[]>, required: true },
});

type BaseTopSheetRef = {
  open: () => void;
  close: () => void;
} | null;

const searchedText = ref<string>("");
const showSearch = ref(false);
const showResponseDropdown = ref<boolean>(false);
const loading = ref<boolean>(false);
const searchResult = ref<Array<ISearchResult> | null>(null);
const topSheetStatus = ref<BaseTopSheetRef>(null);
const isMobileOrTablet = ref<boolean>(false);
const focus = ref<boolean>(false);
const searchApiRoute = ref<string>("/api/search?search=");
const discoverSearch = ref<boolean>(false);
const discoverCityName = ref<string>("");
const searchInCityActivate = ref(false);
const showOverlay = ref(false);
const resetCityFilter = ref<boolean>(false);
const dropdownRef = ref<HTMLElement | null>(null);
let timeout: ReturnType<typeof setTimeout>;

watch(searchedText, (val) => {
  if (searchedText.value.length > 2) {
    loading.value = true;
    if (searchInCityActivate.value) {
      if (isMobileOrTablet.value) {
        focus.value = true;
        topSheetStatus.value?.open();
      } else {
        showResponseDropdown.value = true;
      }
    }
    if (timeout) {
      clearTimeout(timeout);
    }
    // Set a new timeout
    timeout = setTimeout(() => {
      getSearchResult();
    }, 1000);
  } else {
    searchResult.value = [];
    if (searchInCityActivate.value) {
      showResponseDropdown.value = false;
    }
  }
});

watch(
  () => props.isBox,
  (val) => {
    showSearch.value = val;
    searchedText.value = "";
    emit("updateFullWidthStatus", val);
    showResponseDropdown.value = false;
    topSheetStatus.value?.close();
  },
  { immediate: true }
);

watch(
  () => route.path,
  () => {
    discoverCityName.value = "";
    discoverSearch.value = false;
    topSheetStatus.value?.close();
    closeSearchBox();
    checkRoute();
  }
);

async function getSearchResult() {
  searchResult.value = [];
  if (searchedText.value.length) {
    const { data, fetchData } = useCustomFetch(
      searchApiRoute.value + encodeURIComponent(searchedText.value)
    );
    await fetchData();
    const result: IApiResponse<Array<ISearchResult>> | null =
      data.value as IApiResponse<Array<ISearchResult>> | null;
    searchResult.value = result?.data ? result.data : null;
  }
  loading.value = false;
}

const inputFocused = () => {
  showOverlay.value = true;
  if (searchedText.value.length < 3 && searchInCityActivate.value) {
    return;
  } else {
    if (isMobileOrTablet.value) {
      focus.value = true;
      topSheetStatus.value?.open();
    } else {
      showResponseDropdown.value = true;
    }
  }
};

const openSearch = () => {
  if (isMobileOrTablet.value && props.justIcon) {
    topSheetStatus.value?.open();
    setTimeout(() => {
      focus.value = true;
    }, 1000);
  } else {
    if (!props.isBox && !showSearch.value) {
      emit("updateFullWidthStatus", true);
      showSearch.value = true;
      setTimeout(() => {
        focus.value = true;
      }, 1000);
    }
  }
};

const closeSearch = () => {
  if (!isMobileOrTablet.value) {
    closeSearchBox();
  } else return;
};

const closeSearchBox = () => {
  showOverlay.value = false;
  showResponseDropdown.value = false;
  searchedText.value = "";
  searchResult.value = null;
  focus.value = false;
  if (isMobileOrTablet.value) {
    resetCityFilter.value = true;
    discoverCityName.value = "";
    discoverSearch.value = false;
    checkRoute();
  }
  setTimeout(() => {
    resetCityFilter.value = false;
    if (!props.isBox && !searchedText.value.length) {
      showSearch.value = false;
      emit("updateFullWidthStatus", false);
    }
  }, 400);
};

const checkRoute = () => {
  if (
    // Must stay "things-to-do-in-" (with the trailing hyphen), not just
    // "things-to-do" — the new fixed /things-to-do category page's route
    // name also contains "things-to-do" and has no `slug` param, so a bare
    // substring match here would misfire into "search within city" mode.
    route.name?.toString().includes("things-to-do-in-") &&
    !route.params.productSlug
  ) {
    const city: any = route.params.slug;
    discoverCityName.value = city;
    discoverSearch.value = true;
    searchApiRoute.value = `/api/v2/products?page=1&per_page=7&sort=&filter[city]=${discoverCityName.value}&filter[search]=`;
    searchInCityActivate.value = true;
  } else {
    discoverSearch.value = false;
    searchInCityActivate.value = false;
    searchApiRoute.value = "/api/search?search=";
  }
};

const changeSearchApi = (searchInCity: boolean = false) => {
  searchApiRoute.value = searchInCity
    ? `/api/v2/products?page=1&per_page=7&sort=&filter[city]=${discoverCityName.value}&filter[search]=`
    : "/api/search?search=";

  searchInCityActivate.value = searchInCity;
  if (searchInCity && searchedText.value.length < 3) {
    showResponseDropdown.value = false;
  }
  if (searchedText.value.length > 2) {
    loading.value = true;
    getSearchResult();
  }
};

// Dropdown Transition Hooks
const beforeEnter = (el: Element) => {
  const target = el as HTMLElement;
  target.style.height = "0px";
  target.style.opacity = "0";
  target.style.overflow = "hidden";
};

const enter = async (el: Element, done: () => void) => {
  const target = el as HTMLElement;
  target.style.height = "0px";
  target.style.opacity = "0";
  target.style.overflow = "hidden";

  await nextTick();

  const scrollHeight = target.scrollHeight;
  target.style.transition = "height 0.3s ease, opacity 0.2s ease";
  requestAnimationFrame(() => {
    target.style.height = `${scrollHeight}px`;
    target.style.opacity = "1";
  });

  setTimeout(() => {
    target.style.height = "auto";
    target.style.overflow = "";
    done();
  }, 300);
};

const leave = (el: Element, done: () => void) => {
  const target = el as HTMLElement;
  const scrollHeight = target.scrollHeight;

  target.style.height = `${scrollHeight}px`;
  target.offsetHeight; // force repaint

  target.style.transition = "height 0.3s ease, opacity 0.2s ease";
  target.style.height = "0px";
  target.style.opacity = "0";

  setTimeout(() => {
    done();
  }, 300);
};

const handleResize = () => {
  isMobileOrTablet.value = window.innerWidth < 1024;
};

onMounted(() => {
  showSearch.value = props.isBox;
  isMobileOrTablet.value = window.innerWidth < 1024;
  window.addEventListener("resize", handleResize);
  checkRoute();
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
  topSheetStatus.value?.close()
  closeSearchBox()
});

onDeactivated(() => {
  topSheetStatus.value?.close()
  closeSearchBox()
})
</script>
<style scoped>
.input-wrapper .drop-down {
  top: 45px;
  left: 0;
  right: 0;
  width: 100%;
  overflow: hidden;
  will-change: height, opacity;
}
.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.4);
  z-index: 50;
}
.collapse-search-result-enter-active,
.collapse-search-result-leave-active {
  transition: height 0.3s ease;
  /* height: 350px; */
  overflow: hidden;
}

.collapse-search-result-enter-from,
.collapse-search-result-leave-to {
  height: 0;
  transition: height 0.3s ease;
  overflow: hidden;
}
</style>
